import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@repo/ui";
import { Pencil } from "lucide-react";
import { formatDate, formatDateTime } from "@repo/utils";
import { PermissionGate } from "@/components/security";
import type { Dispense } from "@/lib/types/inventory";

export interface DispenseDetailViewProps {
  dispense: Dispense | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (dispense: Dispense) => void;
}

export function DispenseDetailView({
  dispense,
  open,
  onOpenChange,
  onEdit,
}: DispenseDetailViewProps) {
  if (!dispense) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <div>
              <DialogTitle>Dispense Details</DialogTitle>
              <DialogDescription>
                Complete information for RX #{dispense.rx_number}
              </DialogDescription>
            </div>
            {onEdit && (
              <PermissionGate permission="dispenses:edit">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => onEdit(dispense)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              </PermissionGate>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Date Dispensed
              </p>
              <p className="text-base font-semibold">
                {dispense.dispensed_at
                  ? formatDateTime(dispense.dispensed_at)
                  : formatDate(dispense.date, "PPP")}
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Product Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  NDC Code
                </p>
                <p className="text-base font-mono">{dispense.ndc}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Item Name
                </p>
                <p className="text-base font-semibold">
                  {dispense.inventory?.item_name || "Unknown Item"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Quantity Dispensed
                </p>
                <p className="text-base font-semibold">
                  {dispense.quantity_dispensed} units
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Prescription Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  RX Number
                </p>
                <p className="text-base font-mono">{dispense.rx_number}</p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Audit Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Created At
                </p>
                <p className="text-sm">{formatDateTime(dispense.created_at)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Created By
                </p>
                <p className="text-sm">
                  {dispense.created_by_user?.full_name || dispense.created_by}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
